import { v4 as uuidv4 } from 'uuid';
import SlackChannels from '../collections/SlackChannels';
import SlackChannel from '../models/SlackChannel';
import { baseApi } from 'core/api';

const state = () => ({
    _channelItems: {},
    _channelLoading: false,
    _channelLoadingError: false,

    _mappingItem: null,
    _mappingItems: [],
    _mappingLoading: false,
    _mappingLoadingError: false,
    _mappingIsEmpty: true,
    _mappingAddError: false,
    _mappingUpdateError: false,
    _availableInboxes: [],
    _availableChannels: [],
});

const getters = {
    slackChannels: (state) => state._channelItems,
    slackLoading: (state) => state._channelLoading,
    slackLoadingError: (state) => state._channelLoadingError,

    slackMappingItem: (state) => state._mappingItem,
    slackMappingItems: (state) => state._mappingItems,
    slackMappingLoading: (state) => state._mappingLoading,
    slackMappingLoadingError: (state) => state._mappingLoadingError,
    slackMappingAddError: (state) => state._mappingAddError,
    slackMappingUpdateError: (state) => state._mappingUpdateError,
    isEmptySlackMapping: (state) => state._mappingIsEmpty,

    slackAvailableInboxes: (state) => state._availableInboxes,
    slackAvailableChannels: (state) => state._availableChannels,
};

const actions = {
    slackChannelsLoad() {
        if (this._channelLoading) return;

        this._setSlackChannelsLoad();

        return baseApi
            .get('core/core/messenger/channels', {
                params: { integration: 'slack' },
            })
            .then((response) => {
                this._setSlackChannelsLoaded(response.data.channels);
                return response.data.channels;
            })
            .catch((error) => {
                this._setSlackChannelsLoadError(error);
                return error;
            });
    },

    async slackMappingLoad() {
        if (this._mappingLoading) return;

        this._setSlackMappingLoad();

        const channels = await this.slackChannelsLoad();

        return baseApi
            .get('core/core/messenger/mapping', {
                params: { integration: 'slack' },
            })
            .then((response) => {
                this._setSlackMappingLoaded({
                    mapping: response.data.mapping,
                    channels: channels,
                });

                return response.data.mapping;
            })
            .catch((error) => {
                this._setSlackMappingLoadError(error);

                return error;
            });
    },

    slackMappingDelete(params) {
        return baseApi
            .delete('core/core/messenger/mapping/delete', {
                params: { integration: 'slack', ...params },
            })
            .then((response) => {
                this._setSlackMappingDelete(params.channel_id);

                return response;
            })
            .catch((error) => {
                return error;
            });
    },

    addSlackMapping(item) {
        const params = {
            integration: 'slack',
            channel_id: item.channel_id,
            channel_name: item.channelName,
            previous_channel_id: item.previous_channel_id,
            inboxes: item.inboxes,
        };

        return baseApi
            .post('core/core/messenger/mapping/add', params)
            .then((response) => {
                this._setSlackMappingAdd(params);
                return response.data;
            })
            .catch((error) => {
                this._setSlackMappingAddError(error);
                return error;
            });
    },

    createSlackMapping() {
        this._setSlackMappingCreate();
    },

    updateSlackMapping({ id, data }) {
        const params = {
            integration: 'slack',
            channel_id: data.channel_id,
            channel_name: data.channel_name,
            previous_channel_id: data.previous_channel_id,
            inboxes: data.inboxes,
        };

        return baseApi
            .post('core/core/messenger/mapping/add', params)
            .then((response) => {
                this._setSlackMappingUpdate({ id, data });
                return response.data;
            })
            .catch((error) => {
                this._setSlackMappingUpdateError(error);
                return error;
            });
    },

    updateSlackAvailableInboxes(data) {
        this._setUpdateSlackAvailableInboxes(data);
    },

    updateSlackAvailableChannels(data) {
        this._setUpdateSlackAvailableChannels(data);
    },

    updateStateSlack(data) {
        this._setSlackStateUpdate(data);
    },

    // Private methods for all operations
    _setSlackStateUpdate(data) {
        for (let key in data) {
            this[key] = data[key];
        }
    },

    _setSlackChannelsLoad() {
        this._channelLoading = true;
        this._channelLoadingError = false;
    },

    _setSlackChannelsLoaded(payload) {
        this._channelLoading = false;
        this._channelLoadingError = false;
        this._channelItems = payload;
    },

    _setSlackChannelsLoadError() {
        this._channelLoading = false;
        this._channelLoadingError = true;
        this._channelItems = [];
    },

    _setSlackMappingLoad() {
        this._mappingLoading = true;
        this._mappingLoadingError = false;
    },

    _setSlackMappingLoaded({ mapping, channels }) {
        this._mappingLoading = false;
        this._mappingLoadingError = false;

        const arr = [];

        for (let key in mapping) {
            const item = mapping[key];

            arr.push({
                id: uuidv4(),
                channel_id: key,
                previous_channel_id: key,
                inboxes: item,
                channels: channels,
                status: false,
                loading: false,
            });
        }

        const reversedArr = arr.reverse();

        this._mappingItems = new SlackChannels(reversedArr).models;
        this._mappingIsEmpty = Boolean(!reversedArr.length);
    },

    _setSlackMappingCreate() {
        this._mappingItem = new SlackChannel({
            id: uuidv4(),
            channel_id: '',
            previous_channel_id: '',
            inboxes: [],
            channels: this._channelItems,
            status: false,
            loading: false,
        });
    },

    _setSlackMappingAdd(mapping) {
        this._mappingItems = this._mappingItems.push(mapping);
    },

    _setSlackMappingAddError() {
        this._mappingAddError = true;
    },

    _setSlackMappingDelete(id) {
        this._mappingItems = this._mappingItems.filter((item) => item.channel_id !== id);
    },

    _setSlackMappingUpdate({ id, data }) {
        this._mappingItems = this._mappingItems.map((item) => {
            if (item.id === id) {
                for (let key in data) {
                    item[key] = data[key];
                }
            }

            return item;
        });
    },

    _setSlackMappingUpdateError() {
        this._mappingUpdateError = true;
    },

    _setSlackMappingLoadError() {
        this._mappingLoading = false;
        this._mappingLoadingError = true;
        this._mappingItems = [];
    },

    _setUpdateSlackAvailableInboxes({ availableInboxes }) {
        this._availableInboxes = availableInboxes;
    },

    _setUpdateSlackAvailableChannels({ availableChannels }) {
        this._availableChannels = availableChannels;
    },
};

export default {
    state,
    getters,
    actions,
};
